import React from 'react';
import { BaseEdge, EdgeProps, getSmoothStepPath } from 'reactflow';
import { InterfaceType } from '../../types/fta.types';

const CustomFTAEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  data,
  selected,
}: EdgeProps) => {
  const [edgePath] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  // Define styles based on AIAG Interface Types
  let strokeColor = '#64748b'; // Default Slate-500
  let strokeDasharray = 'none';
  let strokeWidth = 2;

  const type = data?.interfaceType || InterfaceType.DEFAULT;

  // Dark mode check - mostly handled by CSS classes or context, 
  // but for canvas stroke colors we often need explicit values or CSS variables.
  // We will use standard colors that work on both or rely on classes if possible, 
  // but BaseEdge style prop expects inline styles.

  switch (type) {
    case InterfaceType.PHYSICAL: // PH - Direct attachment
      strokeColor = '#475569'; // Slate-600
      strokeWidth = 3;
      break;
    case InterfaceType.MATERIAL: // ME - Fluids/Gases
      strokeColor = '#0ea5e9'; // Sky-500
      strokeDasharray = '8, 4'; // Long dashes
      break;
    case InterfaceType.ENERGY: // ET - Power/Force
      strokeColor = '#f59e0b'; // Amber-500
      strokeWidth = 2.5;
      break;
    case InterfaceType.DATA: // DX - Signals
      strokeColor = '#22c55e'; // Green-500
      strokeDasharray = '2, 2'; // Dotted
      break;
    case InterfaceType.HUMAN: // HM - Interaction
      strokeColor = '#a855f7'; // Purple-500
      strokeDasharray = '10, 2, 2, 2'; // Dash-Dot
      break;
    default:
      // Logical/Default
      strokeColor = selected ? '#6366f1' : '#64748b'; // Indigo if selected, else Slate
      strokeWidth = 1.5;
      break;
  }

  // Override color if selected (unless it's the default which handles it above)
  if (selected && type !== InterfaceType.DEFAULT) {
    strokeWidth += 1;
    // We keep the color to distinguish type, but maybe brighten it or add shadow in CSS
  }

  return (
    <>
      {/* Invisible wider path for easier clicking */}
      <BaseEdge
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          strokeWidth: 20,
          stroke: 'transparent',
          cursor: 'pointer'
        }}
      />
      {/* Actual visible path */}
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          ...style,
          stroke: strokeColor,
          strokeWidth,
          strokeDasharray,
        }}
      />
    </>
  );
};

export default React.memo(CustomFTAEdge);
